import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCurrentUser, logoutUser, startGoogleSignIn } from "@/lib/auth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { hash } = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState(null);
  const requestVersion = useRef(0);

  const acceptUser = useCallback((nextUser) => {
    requestVersion.current += 1;
    setUser(nextUser);
    setLoading(false);
    setPrompt(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const nextUser = await getCurrentUser();
      if (version === requestVersion.current) setUser(nextUser);
      return nextUser;
    } catch (error) {
      if (version === requestVersion.current && error.response?.status === 401) setUser(null);
      return null;
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Exchange a new callback session before attempting to validate an old cookie.
    if (new URLSearchParams(hash.slice(1)).has("session_id")) return undefined;
    refreshUser();
    const onFocus = () => refreshUser();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(onFocus, 5 * 60 * 1000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(timer); };
  }, [hash, refreshUser]);

  const signOut = useCallback(async () => {
    await logoutUser();
    acceptUser(null);
  }, [acceptUser]);
  const requireSignIn = useCallback(() => setPrompt({ kind: "limit" }), []);

  return (
    <AuthContext.Provider value={{ user, loading, acceptUser, refreshUser, signOut,
      signIn: startGoogleSignIn, requireSignIn, prompt, setPrompt }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);